// ─── Campagne de calibration guidée ──────────────────────────────────────────
//
// L'écran que suit le technicien : il se place sur un point, attend que les
// trois capteurs passent au vert, passe au suivant. Rien d'autre.
//
// POURQUOI CET ÉCRAN EXISTE
// Une campagne exige que chaque capteur enregistre les trames UNE PAR UNE avec
// leur seq_num — c'est le seul moyen d'apparier la même trame entre capteurs, et
// donc d'obtenir une différence de RSSI d'où la puissance d'émission s'élimine
// exactement. Rien de tout cela n'est visible depuis le terrain : sans retour en
// direct, le technicien ne sait pas s'il a assez attendu, et une campagne ratée
// ne se découvre qu'au dépouillement.
//
// À NE PAS CONFONDRE avec la page Calibration, qui sert l'ANCIENNE méthode :
// un capteur à la fois, des distances mesurées au mètre, l'ajustement de n à
// partir d'un txpower supposé. Cette méthode est abandonnée.
//
// LES POSITIONS SONT APPROXIMATIVES, ET C'EST ASSUMÉ. Le technicien se place à
// l'œil, à ±40 cm près. La méthode qu'on mesure a une erreur de l'ordre de
// 180 cm : l'imprécision du repère est quatre fois plus petite que ce qu'on
// cherche à mesurer, elle s'y noie. Ce qui compte en revanche, c'est qu'il
// puisse dire OÙ IL EST RÉELLEMENT s'il ne peut pas atteindre le point proposé —
// d'où le clic sur le plan.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Chip, Alert, Stack,
  Select, MenuItem, FormControl, InputLabel, TextField, LinearProgress,
  Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Divider,
} from '@mui/material';
import PlayArrowIcon    from '@mui/icons-material/PlayArrow';
import StopIcon         from '@mui/icons-material/Stop';
import CheckCircleIcon  from '@mui/icons-material/CheckCircle';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import MyLocationIcon   from '@mui/icons-material/MyLocation';
import { sitesAPI, campagneAPI } from '../services/api';

// Les points sont proposés en FRACTION du plan, pas en centimètres : la même
// grille sert un séjour de 8 m et un magasin de 30 m. Elle couvre à dessein
// trois situations que la géométrie traite différemment — le centre, les bords,
// et les quatre coins, dont on attend qu'ils tombent hors de l'enveloppe des
// capteurs, là où la méthode ne peut structurellement rien poser.
// Fractions du plan, avec y COMPTE DEPUIS L ENTREE — meme convention que la
// base et que le dessin. Un libelle qui dit « pres de l entree » doit designer
// le meme endroit que la pastille, sinon le technicien marche a l oppose.
const GRILLE = [
  [0.50, 0.50, 'centre'],
  [0.25, 0.30, 'quart gauche, cote entree'],
  [0.75, 0.30, 'quart droit, cote entree'],
  [0.25, 0.70, 'quart gauche, cote fond'],
  [0.75, 0.70, 'quart droit, cote fond'],
  [0.50, 0.15, 'milieu, pres de l entree'],
  [0.50, 0.85, 'milieu, au fond'],
  [0.12, 0.50, 'milieu du mur gauche'],
  [0.88, 0.50, 'milieu du mur droit'],
  [0.12, 0.12, 'coin gauche, entree'],
  [0.88, 0.12, 'coin droit, entree'],
  [0.12, 0.88, 'coin gauche, fond'],
  [0.88, 0.88, 'coin droit, fond'],
];

export default function Campagne() {
  const [sites, setSites]       = useState([]);
  const [siteId, setSiteId]     = useState('');
  const [site, setSite]         = useState(null);
  const [emetteurs, setEmet]    = useState([]);
  const [emetteurId, setEmetId] = useState('');
  const [technicien, setTech]   = useState('');

  const [session, setSession]   = useState(null);
  const [points, setPoints]     = useState([]);   // {label, x, y, id, fait}
  const [idx, setIdx]           = useState(0);
  const [pointId, setPointId]   = useState(null);
  const [etat, setEtat]         = useState(null); // réponse de pointStatus
  const [erreur, setErreur]     = useState('');
  const [occupe, setOccupe]     = useState(false);
  const timer = useRef(null);

  // Les deux endpoints enveloppent leur liste dans un objet : {sites: [...]} et
  // {emitters: [...]}. Le repli `|| r.data` de la premiere version retombait sur
  // l OBJET, et le .filter echouait sans bruit — liste vide, aucun message.
  // D ou cette lecture stricte : on prend le tableau, ou rien.
  const liste = (d, cle) =>
    Array.isArray(d) ? d : (Array.isArray(d?.[cle]) ? d[cle] : []);

  useEffect(() => {
    sitesAPI.list()
      .then(r => setSites(liste(r.data, 'sites')))
      .catch(() => setErreur('Liste des sites indisponible'));
    campagneAPI.emitters()
      .then(r => {
        const tous = liste(r.data, 'emitters');
        const actifs = tous.filter(e => e.is_active);
        setEmet(actifs);
        if (actifs.length === 1) setEmetId(actifs[0].emitter_id);
        if (tous.length && !actifs.length)
          setErreur('Aucun emetteur actif : ' + tous.length +
                    ' declare(s), tous hors service.');
        if (!tous.length)
          setErreur('Aucun emetteur declare. Il en faut un pour ouvrir une campagne.');
      })
      .catch(() => setErreur('Liste des emetteurs indisponible'));
  }, []);

  useEffect(() => {
    if (!siteId) { setSite(null); return; }
    sitesAPI.get(siteId).then(r => setSite(r.data)).catch(() => setSite(null));
  }, [siteId]);

  const L = site?.config?.width_cm  || 0;
  const H = site?.config?.height_cm || 0;
  const capteurs = (site?.sensors || []).filter(s => s.position_mesuree);

  // ─── Ouverture ────────────────────────────────────────────────────────────
  const demarrer = async () => {
    setErreur(''); setOccupe(true);
    try {
      const r = await campagneAPI.start({
        site_id: siteId, technician: technicien || 'non renseigné',
        emitter_id: Number(emetteurId),
      });
      setSession(r.data);
      setPoints(GRILLE.map(([fx, fy, nom], i) => ({
        label: 'P' + (i + 1), nom,
        x: Math.round(fx * L), y: Math.round(fy * H),
        id: null, fait: false,
      })));
      setIdx(0); setPointId(null); setEtat(null);
    } catch (e) {
      setErreur(e.response?.data?.detail || 'Ouverture impossible');
    } finally { setOccupe(false); }
  };

  // ─── Un point ─────────────────────────────────────────────────────────────
  const ouvrirPoint = async () => {
    const p = points[idx];
    setErreur(''); setOccupe(true);
    try {
      // SEUL endroit ou l on quitte le repere du technicien.
      const r = await campagneAPI.openPoint(session.session_id, {
        label: p.label + ' — ' + p.nom, x_cm: p.x, y_cm: versBase(p.y),
      });
      const pid = r.data.point_id;
      setPointId(pid);
      setPoints(ps => ps.map((q, i) => i === idx ? { ...q, id: pid } : q));
      setEtat(null);
    } catch (e) {
      setErreur(e.response?.data?.detail || 'Impossible d’ouvrir le point');
    } finally { setOccupe(false); }
  };

  const sonder = useCallback(async () => {
    if (!session || !pointId) return;
    try {
      const r = await campagneAPI.pointStatus(session.session_id, pointId);
      setEtat(r.data);
    } catch (e) { /* un sondage raté n'interrompt pas la campagne */ }
  }, [session, pointId]);

  useEffect(() => {
    if (!pointId) { clearInterval(timer.current); return; }
    sonder();
    timer.current = setInterval(sonder, 3000);
    return () => clearInterval(timer.current);
  }, [pointId, sonder]);

  const validerPoint = async () => {
    setOccupe(true);
    try {
      await campagneAPI.closePoint(session.session_id, pointId);
      setPoints(ps => ps.map((q, i) => i === idx ? { ...q, fait: true } : q));
      setPointId(null); setEtat(null);
      if (idx < points.length - 1) setIdx(idx + 1);
    } catch (e) {
      setErreur(e.response?.data?.detail || 'Fermeture du point impossible');
    } finally { setOccupe(false); }
  };

  const clore = async () => {
    setOccupe(true);
    try {
      const r = await campagneAPI.close(session.session_id);
      setSession(null); setPointId(null); setEtat(null); setPoints([]);
      setErreur('');
      alert('Campagne close. ' + r.data.capteurs_liberes +
            ' capteur(s) rendus à la mesure normale — ils reprennent au prochain ' +
            'envoi de fenêtre, soit jusqu’à cinq minutes.');
    } catch (e) {
      setErreur(e.response?.data?.detail || 'Clôture impossible');
    } finally { setOccupe(false); }
  };

  // Le technicien ne peut pas toujours atteindre le point propose : un meuble,
  // un passage. Il clique alors la ou il est REELLEMENT. Mieux vaut un point
  // deplace et su qu un point theorique et faux.
  const deplacerPoint = (ev) => {
    if (!L || !H || pointId) return;
    const r = ev.currentTarget.getBoundingClientRect();
    // Repere de MESURE : x croit vers la droite de l ecran, y depuis le bas.
    const x = Math.round((ev.clientX - r.left) / r.width * L);
    const y = Math.round((1 - (ev.clientY - r.top) / r.height) * H);
    setPoints(ps => ps.map((q, i) => i === idx ? { ...q, x, y, nom: 'position choisie' } : q));
  };

  // PROJECTION — le piege qui m a eu deux fois dans la journee.
  // La base stocke y = 0 A L ENTREE, c est-a-dire en HAUT du plan (convention de
  // Flormar, ou le capteur d entree est a 290 cm de la porte). Dessiner avec
  // `bottom: y` place donc les capteurs a l envers : ADE4, a 70 cm du mur du
  // bas, apparaissait a 93 % de hauteur. On projette explicitement, une seule
  // fois, et tout le reste s aligne dessus.
  // ── UN SEUL REPERE A L ECRAN : CELUI DU TECHNICIEN ────────────────────────
  //
  // Deux reperes coexistaient, et les melanger a coute une serie de points.
  //   - repere de MESURE, celui du plan papier et du terrain : origine a un coin
  //     de la piece, y qui MONTE vers l entree ;
  //   - repere de la BASE : y compte depuis l ENTREE, donc depuis le haut, par
  //     coherence avec Flormar ou le capteur d entree est a 290 cm de la porte.
  //
  // L ecran travaille entierement dans le repere de MESURE. Une seule paire de
  // fonctions fait la bascule, et rien d autre ne touche aux coordonnees.
  const versMesure = (yBase) => H - yBase;
  const versBase   = (yMes)  => H - yMes;

  // PAS de miroir sur x : l origine est en BAS A GAUCHE de l ecran, x croit
  // vers la droite. C est l orientation que Yassir voit depuis la piece, et
  // c est elle qui fait foi — un repere dont le technicien doit retourner le
  // sens dans sa tete est un repere faux, quelle que soit la convention.
  const px = (x)    => (100 * x / L).toFixed(2) + '%';
  // y de mesure : 0 EN BAS de l ecran, comme sur le plan papier.
  const py = (yMes) => (100 * (H - yMes) / H).toFixed(2) + '%';
  const courant = points[idx];
  const complet = etat?.complet;

  // ─── Rendu ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>Campagne de calibration</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 760 }}>
        L’émetteur de référence est posé sur chaque point, immobile, jusqu’à ce que
        tous les capteurs passent au vert. Les positions sont approximatives : la
        méthode mesurée a une erreur de l’ordre du mètre, quelques dizaines de
        centimètres d’imprécision sur le repère s’y noient.
      </Typography>

      {erreur && <Alert severity="error" sx={{ mb: 2 }}>{erreur}</Alert>}

      {!session && (
        <Card sx={{ mb: 2, maxWidth: 760 }}><CardContent>
          <Stack spacing={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Site</InputLabel>
              <Select value={siteId} label="Site" onChange={e => setSiteId(e.target.value)}>
                {sites.map(s => (
                  <MenuItem key={s.site_id} value={s.site_id}>
                    {s.site_id} — {s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel>Émetteur</InputLabel>
              <Select value={emetteurId} label="Émetteur"
                      onChange={e => setEmetId(e.target.value)}>
                {emetteurs.map(e => (
                  <MenuItem key={e.emitter_id} value={e.emitter_id}>
                    {e.label} — {e.mac} — canal {e.channel}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField size="small" label="Technicien" value={technicien}
                       onChange={e => setTech(e.target.value)} />

            {site && (
              <Alert severity={capteurs.length >= 3 ? 'info' : 'warning'}>
                {capteurs.length} capteur(s) positionné(s) sur {L}×{H} cm.
                {capteurs.length < 3 && ' Il en faut au moins trois, non alignés : ' +
                  'deux capteurs ne donnent qu’une différence de RSSI pour deux ' +
                  'inconnues, aucune position n’est calculable.'}
              </Alert>
            )}

            <Button variant="contained" startIcon={<PlayArrowIcon />}
                    disabled={!siteId || !emetteurId || occupe || !L}
                    onClick={demarrer}>
              Démarrer la campagne
            </Button>
            <Typography variant="caption" color="text.secondary">
              L’ouverture bascule tous les capteurs du site en mode calibration sur
              la MAC de cet émetteur. Ils prennent l’ordre au prochain envoi de
              fenêtre : compter jusqu’à cinq minutes avant le premier point.
            </Typography>
          </Stack>
        </CardContent></Card>
      )}

      {session && (
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="flex-start">

          {/* ── le plan ── */}
          <Box>
            <Box onClick={deplacerPoint}
                 sx={{ position: 'relative', width: 340, aspectRatio: `${L} / ${H}`,
                       border: '2px solid', borderColor: 'divider',
                       bgcolor: 'background.paper',
                       cursor: pointId ? 'default' : 'crosshair' }}>
              {/* L ORIGINE, marquee explicitement. Sans repere visible, rien ne
                  dit de quel cote se trouve le (0,0), et l orientation se devine
                  — c est exactement ce qui a coute une serie de points. */}
              <Box sx={{ position: 'absolute', left: 0, bottom: 0,
                         width: 34, height: 34,
                         borderRight: '2px solid', borderTop: '2px solid',
                         borderColor: 'error.main', pointerEvents: 'none' }} />
              <Box sx={{ position: 'absolute', left: 4, bottom: 36, fontSize: 10,
                         fontWeight: 700, color: 'error.main', pointerEvents: 'none' }}>
                0,0
              </Box>
              <Box sx={{ position: 'absolute', left: 38, bottom: 8, fontSize: 9,
                         color: 'error.main', pointerEvents: 'none' }}>
                x &#8594;
              </Box>
              <Box sx={{ position: 'absolute', left: 8, bottom: 52, fontSize: 9,
                         color: 'error.main', pointerEvents: 'none' }}>
                y &#8593;
              </Box>
              {capteurs.map(c => (
                <Tooltip key={c.sensor_id}
                         title={`${c.sensor_id} — ${c.x_cm} cm du mur gauche, `
                                + `${versMesure(c.y_cm)} cm de l'origine`}>
                  <Box sx={{ position: 'absolute', width: 14, height: 14,
                             ml: '-7px', mt: '-7px', borderRadius: '50%',
                             border: '3px solid', borderColor: 'primary.main',
                             bgcolor: 'background.paper',
                             left: px(c.x_cm), top: py(versMesure(c.y_cm)) }} />
                </Tooltip>
              ))}
              {points.map((p, i) => (
                <Box key={p.label} sx={{
                  position: 'absolute', width: i === idx ? 26 : 18,
                  height: i === idx ? 26 : 18,
                  ml: i === idx ? '-13px' : '-9px', mt: i === idx ? '-13px' : '-9px',
                  borderRadius: '50%', display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  fontSize: i === idx ? 12 : 10, fontWeight: 600, color: '#fff',
                  bgcolor: p.fait ? 'success.main' : (i === idx ? 'error.main' : 'grey.500'),
                  border: '2px solid', borderColor: 'background.paper',
                  boxShadow: i === idx ? 3 : 0,
                  left: px(p.x), top: py(p.y) }}>
                  {i + 1}
                </Box>
              ))}
            </Box>
            <Typography variant="caption" display="block" color="text.secondary">
              L’origine (0,0) est en BAS À GAUCHE : x augmente vers la droite,
              y vers le haut. C’est l’orientation vue depuis la pièce.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {pointId
                ? 'Mesure en cours — ne pas déplacer l’émetteur.'
                : 'Clique sur le plan pour déplacer le point si tu ne peux pas l’atteindre.'}
            </Typography>
          </Box>

          {/* ── le guidage ── */}
          <Box sx={{ flex: 1, minWidth: 320 }}>
            <Card sx={{ mb: 2 }}><CardContent>
              <Typography variant="overline" color="text.secondary">
                Point {idx + 1} sur {points.length}
              </Typography>
              <Typography variant="h6">
                {courant?.label} — {courant?.nom}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                à environ {(courant?.x / 100).toFixed(1)} m du mur gauche et{' '}
                {(courant?.y / 100).toFixed(1)} m de l’origine
              </Typography>

              {!pointId && (
                <Button variant="contained" startIcon={<MyLocationIcon />}
                        disabled={occupe} onClick={ouvrirPoint}>
                  Je suis en place
                </Button>
              )}

              {pointId && !etat && <LinearProgress sx={{ my: 2 }} />}

              {pointId && etat && (
                <>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                    {etat.capteurs.map(c => (
                      <Tooltip key={c.sensor_id} title={
                        c.releves
                          ? `${c.releves} relevés · RSSI ${c.rssi_moyen} dBm · erreur type ${c.erreur_type} dB`
                          : 'aucun relevé — ce capteur n’entend pas l’émetteur'
                      }>
                        <Chip
                          icon={c.suffisant ? <CheckCircleIcon /> : <HourglassTopIcon />}
                          color={c.suffisant ? 'success' : 'default'}
                          variant={c.suffisant ? 'filled' : 'outlined'}
                          label={`${c.sensor_id.replace('REDIA-', '')} · ${c.releves}`}
                        />
                      </Tooltip>
                    ))}
                  </Stack>

                  {complet
                    ? <Alert severity="success" sx={{ mb: 2 }}>
                        Point complet — tu peux passer au suivant.
                      </Alert>
                    : <Alert severity="info" sx={{ mb: 2 }}>
                        Reste immobile. Il faut {etat.criteres.releves_min} relevés
                        par capteur et une erreur type sous {etat.criteres.erreur_type_max_db} dB.
                        Un capteur à zéro ne l’entend pas : vérifie qu’il est sur le
                        même canal que l’émetteur.
                      </Alert>}

                  <Button variant="contained" color={complet ? 'success' : 'inherit'}
                          endIcon={<NavigateNextIcon />} disabled={occupe}
                          onClick={validerPoint}>
                    {complet ? 'Point suivant' : 'Passer quand même'}
                  </Button>
                </>
              )}
            </CardContent></Card>

            <Card sx={{ mb: 2 }}><CardContent sx={{ py: 1 }}>
              <Table size="small">
                <TableHead><TableRow>
                  <TableCell>Point</TableCell><TableCell>Position</TableCell>
                  <TableCell>État</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {points.map((p, i) => (
                    <TableRow key={p.label} selected={i === idx}>
                      <TableCell>{p.label}</TableCell>
                      <TableCell>{(p.x / 100).toFixed(1)} · {(p.y / 100).toFixed(1)} m</TableCell>
                      <TableCell>
                        {p.fait ? <Chip size="small" color="success" label="fait" />
                                : i === idx ? <Chip size="small" color="error" label="en cours" />
                                : <Chip size="small" variant="outlined" label="à faire" />}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent></Card>

            <Divider sx={{ my: 2 }} />
            <Button variant="outlined" color="error" startIcon={<StopIcon />}
                    disabled={occupe} onClick={clore}>
              Clore la campagne
            </Button>
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
              À ne pas oublier : la clôture rend les capteurs à la mesure normale.
              Un capteur laissé en calibration n’enregistre que l’émetteur — il ne
              compte plus personne.
            </Typography>
          </Box>
        </Stack>
      )}
    </Box>
  );
}
